import React, { useState } from "react";
import { ActionIcon, Button, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { randomId } from "@mantine/hooks";
import { closeModal, ContextModalProps, openContextModal } from "@mantine/modals";
import { FaExclamationTriangle as IconAlertTriangle, FaTimes as IconClose } from "react-icons/fa";

export const ACCOUNT_ACCESS_ISSUE_TITLE = "Account access issue";
export const ACCOUNT_ACCESS_ISSUE_PLAY_LABEL = "Play";
export const ACCOUNT_ACCESS_ISSUE_BODY_PARAGRAPHS = [
	"Facebook is moving Instant Games to a new privacy model (Zero Permissions). During this transition it can't pass your account identifier to the game for some players, so we can't recognize you and your progress may appear reset.",
	"This is on Facebook's side, not the game — we've already reported it. You can start again and keep playing; to get your progress back, our support can restore your account. You can keep playing in the meantime."
] as const;

type AccountAccessIssueModalInnerProps = {
	onPlay: () => Promise<void>;
	onRequestClose: () => void;
};

/**
 * Открывает диалог и гарантирует единственный результат попытки повторной аутентификации.
 */
export function openAccountAccessIssueModal(params: {
	onPlay: () => Promise<void>;
}): Promise<boolean> {
	return new Promise<boolean>((resolve, reject) => {
		let settled = false;
		let playInProgress = false;
		const modalId = `account-access-issue-${randomId()}`;

		/**
		 * Завершает ожидание отказом только когда повторная аутентификация не выполняется.
		 */
		const settleClosed = () => {
			if (!settled && !playInProgress) {
				settled = true;
				resolve(false);
			}
		};

		/**
		 * Закрывает диалог по управляемой кнопке, не прерывая уже начатый повторный вход.
		 */
		const onRequestClose = () => {
			if (!playInProgress) {
				settleClosed();
				closeModal(modalId);
			}
		};

		/**
		 * Выполняет повторную аутентификацию и завершает результат диалога её исходом.
		 */
		const onPlay = async () => {
			if (settled || playInProgress) return;
			playInProgress = true;
			try {
				await params.onPlay();
				settled = true;
				resolve(true);
			} catch (error) {
				settled = true;
				reject(error);
			} finally {
				closeModal(modalId);
			}
		};

		openContextModal({
			modal: "accountAccessIssue",
			modalId,
			innerProps: { onPlay, onRequestClose },
			onClose: settleClosed,
			centered: true,
			withCloseButton: false,
			closeOnClickOutside: false,
			closeOnEscape: false
		});
	});
}

/**
 * Показывает объяснение проблемы доступа и запускает повторную аутентификацию после подтверждения игрока.
 */
export const AccountAccessIssueModal = ({
	innerProps
}: ContextModalProps<AccountAccessIssueModalInnerProps>) => {
	const [loading, setLoading] = useState(false);

	/**
	 * Запускает повторную аутентификацию и блокирует элементы диалога до её завершения.
	 */
	const play = async () => {
		if (loading) return;
		setLoading(true);
		try {
			await innerProps.onPlay();
		} catch {
			// Ошибка уже передана promise-контроллером в основной обработчик авторизации.
		}
	};

	/**
	 * Закрывает диалог до запуска повторной аутентификации.
	 */
	const requestClose = () => {
		if (!loading) {
			innerProps.onRequestClose();
		}
	};

	return (
		<Stack align="center" spacing="md" style={{ position: "relative" }}>
			<ActionIcon
				aria-label="Close"
				color="gray"
				disabled={loading}
				onClick={requestClose}
				style={{ position: "absolute", right: 0, top: 0 }}
				variant="subtle"
			>
				<IconClose aria-hidden />
			</ActionIcon>
			<Title order={3} align="center">
				{ACCOUNT_ACCESS_ISSUE_TITLE}
			</Title>
			<ThemeIcon color="yellow" variant="light" radius="xl" size={56}>
				<IconAlertTriangle size={30} aria-hidden />
			</ThemeIcon>
			{ACCOUNT_ACCESS_ISSUE_BODY_PARAGRAPHS.map((paragraph) => (
				<Text key={paragraph} align="center">
					{paragraph}
				</Text>
			))}
			<Button fullWidth loading={loading} disabled={loading} onClick={() => void play()}>
				{ACCOUNT_ACCESS_ISSUE_PLAY_LABEL}
			</Button>
		</Stack>
	);
};
